import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { House, ChatCircleDots, Toolbox, UserCircle, Eye } from "@phosphor-icons/react";

/**
 * Mobile-only bottom tab bar.
 *
 * iOS variant: adds a floating "Visualise" pill in the CENTER that launches
 * the Astro Visualization cosmic-journey tool. Detected via the
 * `data-ak-platform="ios-app"` attribute that AppDelegate.swift's patchJS
 * sets on <html> at boot — so the website is untouched.
 *
 * The button intentionally breaks out ABOVE the pill so it feels like
 * Instagram's Reels button or AstroTalk's mic-call CTA. Tapping it routes to
 * /visualise which plays a cosmic loading animation and then hands off to
 * /astro-visualization.
 */
const TABS = [
  { key: "home",  label: "Home",    to: "/",            icon: House },
  { key: "chat",  label: "Chat",    to: "/astrologers", icon: ChatCircleDots },
  { key: "tools", label: "Tools",   to: "/tools",       icon: Toolbox },
  { key: "you",   label: "Profile", to: "/dashboard",   icon: UserCircle },
];

function useIsIOSApp() {
  const [is, setIs] = React.useState(false);
  React.useEffect(() => {
    const check = () =>
      setIs(document.documentElement.getAttribute("data-ak-platform") === "ios-app");
    check();
    // patchJS may set the attribute after React mounts; watch briefly.
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-ak-platform"] });
    return () => obs.disconnect();
  }, []);
  return is;
}

export default function MobileBottomTabBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isIOSApp = useIsIOSApp();

  const isActive = (to) => {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(to + "/");
  };

  // Split tabs into [left, right] halves so we can slot the Visualise button in the middle on iOS.
  const midpoint = Math.ceil(TABS.length / 2);
  const leftTabs = isIOSApp ? TABS.slice(0, midpoint) : TABS;
  const rightTabs = isIOSApp ? TABS.slice(midpoint) : [];

  const renderTab = (tab) => {
    const active = isActive(tab.to);
    const Icon = tab.icon;
    return (
      <Link
        key={tab.key}
        to={tab.to}
        aria-label={tab.label}
        aria-current={active ? "page" : undefined}
        data-testid={`bottom-tab-${tab.key}`}
        className="group relative flex-1 flex flex-col items-center justify-center py-1.5 select-none"
      >
        <span
          className={`absolute inset-x-3 inset-y-0.5 rounded-2xl transition-all duration-300 ease-out ${
            active
              ? "bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 ring-1 ring-orange-200/70 scale-100 opacity-100"
              : "bg-transparent scale-90 opacity-0"
          }`}
          aria-hidden="true"
        />
        <span
          className={`relative z-10 transition-transform duration-300 ${
            active ? "-translate-y-0.5 scale-110" : "scale-100 group-active:scale-95"
          }`}
        >
          <Icon
            size={22}
            weight={active ? "fill" : "regular"}
            className={active ? "text-orange-600" : "text-stone-500"}
          />
        </span>
        <span
          className={`relative z-10 mt-0.5 text-[10px] font-semibold tracking-wide transition-colors ${
            active ? "text-orange-700" : "text-stone-600"
          }`}
        >
          {tab.label}
        </span>
        <span
          className={`relative z-10 mt-0.5 h-1 w-1 rounded-full transition-all duration-300 ${
            active ? "bg-orange-500 opacity-100 scale-100" : "opacity-0 scale-50"
          }`}
          aria-hidden="true"
        />
      </Link>
    );
  };

  return (
    <nav
      className="sm:hidden fixed inset-x-0 bottom-0 z-50 pointer-events-none"
      aria-label="Primary"
      data-testid="mobile-bottom-tab-bar"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="pointer-events-auto mx-3 mb-3 relative">
        {/* Decorative glow halo */}
        <div
          aria-hidden="true"
          className="absolute -inset-x-2 bottom-1 h-16 rounded-[36px] blur-xl opacity-60"
          style={{
            background:
              "radial-gradient(60% 100% at 50% 100%, rgba(251, 146, 60, 0.35) 0%, rgba(244, 63, 94, 0.18) 45%, transparent 75%)",
          }}
        />

        <div
          className="relative flex items-center justify-around gap-1 rounded-[28px] border border-white/40 bg-white/85 backdrop-blur-xl shadow-[0_10px_40px_-12px_rgba(63,45,27,0.35)] px-2 py-1.5"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(253,247,234,0.88) 100%)",
          }}
        >
          {leftTabs.map(renderTab)}

          {/* iOS-only: center floating "Visualise" button. Web keeps 4 tabs unchanged. */}
          {isIOSApp && <span className="w-[76px] flex-shrink-0" aria-hidden="true" />}

          {rightTabs.map(renderTab)}
        </div>

        {/* Floating Visualise CTA — sits ABOVE the tab pill, iOS-only.
            Blue-eye / nazar-inspired design: cobalt-to-indigo radial base,
            crystalline white iris ring, deep-blue pupil, rotating halo,
            slow-pulsing outer aura. Reads as a mystical "third eye" that
            invites the user to see their cosmic road. */}
        {isIOSApp && (
          <button
            type="button"
            onClick={() => navigate("/visualise")}
            aria-label="Open Astro Visualise"
            data-testid="bottom-tab-visualise"
            className="absolute left-1/2 -translate-x-1/2 -top-8 w-[76px] h-[76px] rounded-full flex flex-col items-center justify-center active:scale-95 transition-transform"
            style={{
              // 3-stop deep-cobalt radial base
              background:
                "radial-gradient(circle at 50% 35%, #6EA9FF 0%, #1E5FDA 40%, #0B2B7A 80%, #061B4E 100%)",
              boxShadow:
                "0 12px 32px -8px rgba(30, 95, 218, 0.75), 0 0 0 4px #FFF8E7, 0 0 22px 4px rgba(110, 169, 255, 0.55)",
              border: "1px solid rgba(255, 255, 255, 0.35)",
            }}
          >
            {/* Slow-pulsing outer aura — mystical breathing halo */}
            <span
              aria-hidden="true"
              className="absolute inset-[-6px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(110,169,255,0.55) 0%, transparent 70%)",
                animation: "vizAura 2.4s ease-in-out infinite",
              }}
            />

            {/* Rotating conic halo — subtle "scanning" motion */}
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full opacity-70"
              style={{
                background:
                  "conic-gradient(from 0deg, rgba(255,255,255,0.75), transparent 25%, rgba(110,169,255,0.9) 55%, transparent 80%, rgba(255,255,255,0.6))",
                animation: "spin 4s linear infinite",
                WebkitMask: "radial-gradient(closest-side, transparent 62%, black 65%)",
                        mask: "radial-gradient(closest-side, transparent 62%, black 65%)",
              }}
            />

            {/* The BLUE EYE — Phosphor duotone icon with a custom pupil dot */}
            <span className="relative z-10 flex flex-col items-center leading-none">
              <span className="relative">
                <Eye size={30} weight="duotone" style={{ color: "#FFFFFF" }} />
                {/* Bright cobalt pupil pop — placed dead-center of the iris */}
                <span
                  aria-hidden="true"
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    width: 7,
                    height: 7,
                    background:
                      "radial-gradient(circle, #FFFFFF 0%, #6EA9FF 40%, #0B2B7A 100%)",
                    boxShadow: "0 0 8px rgba(110, 169, 255, 0.9)",
                    animation: "vizPupil 2.4s ease-in-out infinite",
                  }}
                />
              </span>
              <span
                className="text-[8.5px] font-black tracking-[0.14em] uppercase mt-1"
                style={{
                  color: "#FFF8E7",
                  textShadow: "0 1px 4px rgba(0,0,0,0.6)",
                }}
              >
                Visualise
              </span>
            </span>
          </button>
        )}
      </div>

      {/* Animations for the Visualise button — CSS-only, scoped globally
          via <style> so no Tailwind config change is needed. */}
      <style>{`
        @keyframes vizAura {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.08); }
        }
        @keyframes vizPupil {
          0%, 100% { transform: translate(-50%, -50%) scale(1);    filter: brightness(1); }
          50%      { transform: translate(-50%, -50%) scale(1.35); filter: brightness(1.6); }
        }
      `}</style>
    </nav>
  );
}
